import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device'
import { LoadingController, ToastController, PopoverController, AlertController } from '@ionic/angular';
import { Constants } from 'src/app/config/constants';
import { StorageService } from 'src/app/lib/services/storage.service';
import { CommonApi } from './api/common.api';
import { HelpVideoComponent } from 'src/app/components/help-video/help-video.component';
import { ModalController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { LogoutPopUpComponent } from 'src/app/components/logout-pop-up/logout-pop-up.component';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  state = false;
  userData: any;
  isLoading: boolean;
  appCodeVersion: any;
  appCodeVersionName: any;
  remoteAppVersionName: any;
  remoteAppUpdateLink: any;
  hasCurrentLocation: boolean = false;
  deviceId: any;
  taskCompleted: boolean = false;
  subject = new Subject<boolean>();
  isToastVisible: boolean;

  constructor(
    private loadingController: LoadingController,
    private toastController: ToastController,
    private alertController: AlertController,
    public popoverCtrl: PopoverController,
    private storageService: StorageService,
    private commonApi: CommonApi,
    private modalCtrl: ModalController
  ) {
    if (this.storageService.getItem(Constants.AUTH_TOKEN)) {
      this.setUserData();
    }
    this.getDeviceId();
  }

  async presentLoader(): Promise<HTMLIonLoadingElement> {
    return await this.loadingController.create({
      mode: 'ios',
      animated: true,
      spinner: 'bubbles',
    });
  }

  async dismissLoader() {
    return await this.loadingController.dismiss().then(() => '');
  }

  async getDeviceInfo() {
    await Device.getInfo();
  }

  async getDeviceId() {
    this.deviceId = await Device.getId();
  }

  getStoreDetails(id: any) {
    return this.commonApi.getStoreDetails(id);
  }

  setUserData() {
    this.userData = this.parseJwt(this.storageService.getItem(Constants.AUTH_TOKEN));
  }

  getUserData() {
    return this.parseJwt(this.storageService.getItem(Constants.AUTH_TOKEN));
  }

  parseJwt(token: any) {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        throw new Error('Invalid token format: Missing payload');
      }
      // Decode the payload
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window.atob(base64).split('').map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join('')
      );
      // Parse and return the JSON payload
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null; // or handle the error as needed
    }
  }

  // async present() {
  //   const message = this.getTranslatedMessage('Loading...') || 'Loading...';
  //   this.state = true;
  //   return await this.loadingController.create({
  //     message,
  //     spinner: 'lines',
  //   }).then((a) => {
  //     a.present().then(() => {
  //       if (!this.state) {
  //         a.dismiss();
  //       }
  //     });
  //   });
  // }

  // async dismiss() {
  //   this.state = false;
  //   while ((await this.loadingController.getTop()) !== undefined) {
  //     await this.loadingController.dismiss();
  //   }
  // }

  // async presentLoading() {
  //   const message = this.getTranslatedMessage('Loading...') || 'Loading...';
  //   const that = this;
  //   this.isLoading = true;
  //   return await this.loadingController.create({
  //     message,
  //     mode: 'ios',
  //     spinner: 'circles',
  //   }).then(a => {
  //     a.present().then(() => {
  //       if (!this.isLoading) {
  //         a.dismiss().then(() => '');
  //       }
  //     });
  //   });
  // }

  presentProgressBarLoading() {
    this.isLoading = true;
  }

  closeProgressBarLoading() {
    this.isLoading = false;
  }

  // async dissmiss_loading() {
  //   if (this.isLoading) {
  //     this.isLoading = false;
  //     return await this.loadingController.dismiss().then(() => '');
  //   }
  //   return null;
  // }

  getTranslatedMessage(key: string): string | undefined {
    const selectedLanguage = this.storageService.getItem(Constants.ALL_LANGUAGES);
    if (selectedLanguage) return selectedLanguage[key];
    return undefined;
  }

  async presentToast(msg: string) {
    const message = this.getTranslatedMessage(msg) || msg;
    const toast = await this.toastController.create({
      message,
      duration: 3000,
    });
    toast.present();
  }

  async success(msg: string) {
    const message = this.getTranslatedMessage(msg) || msg;
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color: 'success',
    });
    toast.present();
  }

  async danger(msg: string) {
    const message = this.getTranslatedMessage(msg) || msg;
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color: 'danger',
    });
    toast.present();
  }

  async errorToast(error: any) {
    if (error && error.status && error.error.error.message) {
      const { error: { error: { message } } } = error;
      this.danger(message);
    } else {
      this.danger('Something went wrong! Please try again later');
    }
  }

  toast(msg: string, cssClass = '') {
    const message = this.getTranslatedMessage(msg) || msg;
    this.toastController.create({
      message,
      mode: 'ios',
      color: 'dark',
      duration: 3000,
      cssClass,
    }).then((toast) => toast.present());
  }

  alert(headers: string, msg: string, okText: string, cancelText: string,
    okAction: () => void, cancelAction: () => void) {

    const header = this.getTranslatedMessage(headers) || headers;
    const message = this.getTranslatedMessage(msg) || msg;
    const okLabel = this.getTranslatedMessage(okText) || okText;
    const cancelLabel = this.getTranslatedMessage(cancelText) || cancelText;

    const buttons = [];
    if (okLabel) {
      buttons.push({ text: okLabel, handler: okAction });
    }
    if (cancelLabel) {
      buttons.push({
        text: cancelLabel,
        role: 'cancel',
        handler: cancelAction,
      });
    }
    this.alertController.create({
      header,
      message,
      buttons,
      mode: 'ios',
      backdropDismiss: false
    }).then((alert) => alert.present());
  }

  featureNotAvailable() {
    this.presentToast('Feature not available.');
  }

  referToRaApp() {
    this.presentToast('Please refer to Retailer App.');
  }

  createArrayofObject(data: any): any {
    const arrayOfObj = {};
    if (data) {
      data.forEach((v: any) => {
        arrayOfObj[v.id] = v;
      });
    }
    return arrayOfObj;
  }

  async presentLoaderForHelp(loading: any) {
    return await loading.present();
  }

  async openHelpVideo(helpVideos: { title: string, id: string }[]) {
    const message = this.getTranslatedMessage('Loading...') || 'Loading...';
    const loading = await this.loadingController.create({
      message,
      mode: 'ios',
      spinner: 'circles',
    });
    this.presentLoaderForHelp(loading);
    const breakpoint: number = helpVideos.length === 1 ? 0.45 : helpVideos.length === 2 ? 0.75 : 0.90;
    const model = await this.modalCtrl.create({
      component: HelpVideoComponent,
      breakpoints: [0, breakpoint],
      initialBreakpoint: breakpoint,
      mode: 'ios',
      componentProps: {
        helpVideos: helpVideos
      }
    });
    await model.present();
    loading.dismiss();
  }

  async customAlert(header: any, subHeader: any, yesCallBack: any, noCallBack: any, forBack?: any) {
    if (forBack ? this.taskCompleted : true) {
      const model = await this.modalCtrl.create({
        component: LogoutPopUpComponent,
        cssClass: 'cancel-order-modal',
        componentProps: {
          header: header,
          subHeader: subHeader,
          forBack: forBack,
        }
      })
      await model.present()
      const { data } = await model.onWillDismiss()
      if (data.status == 'Yes') {
        if (forBack) {
          this.subject.next(true);
        } else {
          this.subject.next(false);
          yesCallBack();
        }
      } else {
        noCallBack();
      }
    } else {
      setTimeout(() => {
        this.subject.next(true);
      }, 200)
    }
  }

  async presentAlert(
    header: string,
    message: string,
    buttons: any[] = ['OK'],
    inputs: any[] = [],
    cssClass: string = ''
  ): Promise<HTMLIonAlertElement> {
    const alert = await this.alertController.create({
      header,
      message,
      buttons,
      inputs,
      cssClass,
      mode: 'ios'
    });

    await alert.present();
    return alert;
  }

  toastMessage(msg: string, cssClass = '') {
    const message = this.getTranslatedMessage(msg) || msg;
    this.toastController.create({
      message,
      mode: 'ios',
      color: 'dark',
      duration: 3000,
      cssClass
    }).then((toast) => toast.present());
  }

  async errorMessage(error: any) {
    if (error && error.status && error.error.error.message) {
      const { error: { error: { message } } } = error;
      this.errorToast(message);
    } else {
      this.errorToast('Something went wrong! Please try again later');
    }
  }
}
